import { formatMeetingTime } from '../../utils/meetingSchedule';
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Today,
  Edit,
  Cancel,
  CheckCircle
} from '@mui/icons-material';
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
  isToday,
  isBefore,
  startOfDay,
  startOfWeek,
  endOfWeek
} from 'date-fns';
import { es } from 'date-fns/locale';

import {
  schedulingService,
  ScheduledMeeting,
  MeetingStatus,
  MeetingFilter,
  DailyAvailability
} from '../../services/schedulingService';

interface MeetingCalendarProps {}

const MeetingCalendar: React.FC<MeetingCalendarProps> = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
  const [availability, setAvailability] = useState<DailyAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(startOfDay(new Date()));
  const [agendaQuery, setAgendaQuery] = useState('');
  const [agendaStatus, setAgendaStatus] = useState<'all' | MeetingStatus>('all');
  const [selectedMeeting, setSelectedMeeting] = useState<ScheduledMeeting | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<MeetingStatus>(MeetingStatus.SCHEDULED);
  const [statusNotes, setStatusNotes] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  useEffect(() => {
    if (!selectedDate) return;
    setCurrentDate((prev) => (isSameMonth(prev, selectedDate) ? prev : selectedDate));
  }, [selectedDate]);

  const loadCalendarData = async () => {
    try {
      const shouldBlock = !hasLoadedRef.current;
      if (shouldBlock) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);

      // Load meetings for the month
      const filters: MeetingFilter = {
        start_date: format(monthStart, 'yyyy-MM-dd'),
        end_date: format(monthEnd, 'yyyy-MM-dd')
      };

      const meetingsData = await schedulingService.getScheduledMeetings(filters, 0, 1000);
      setMeetings(meetingsData);

    } catch (err) {
      console.error('Error loading calendar data:', err);
      setError('Error al cargar los datos del calendario');
    } finally {
      if (!hasLoadedRef.current) {
        setLoading(false);
        hasLoadedRef.current = true;
      }
      setRefreshing(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    const today = startOfDay(new Date());
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const handleMeetingClick = (meeting: ScheduledMeeting) => {
    setSelectedMeeting(meeting);
    setDetailDialogOpen(true);
  };

  const handleStatusUpdate = () => {
    if (selectedMeeting) {
      setNewStatus(selectedMeeting.status as MeetingStatus);
      setStatusNotes('');
      setCancellationReason('');
      setStatusUpdateDialogOpen(true);
    }
  };

  const handleSaveStatusUpdate = async () => {
    if (!selectedMeeting) return;

    try {
      await schedulingService.updateMeetingStatus(
        selectedMeeting.id,
        newStatus,
        statusNotes || undefined,
        cancellationReason || undefined
      );

      // Reload data
      await loadCalendarData();
      
      // Update selected meeting
      const updatedMeeting = await schedulingService.getScheduledMeeting(selectedMeeting.id);
      setSelectedMeeting(updatedMeeting);
      
      setStatusUpdateDialogOpen(false);
    } catch (err) {
      console.error('Error updating meeting status:', err);
      setError('Error al actualizar el estado de la cita');
    }
  };

  const getMeetingsForDate = (date: Date): ScheduledMeeting[] => {
    const dateStr = format(date, 'yyyy-MM-dd');

    const toMinutes = (time: string | null | undefined) => {
      const raw = (time ?? '').toString().trim();
      if (!raw) return Number.POSITIVE_INFINITY;
      const parts = raw.split(':');
      const hours = Number(parts[0]);
      const minutes = Number(parts[1] ?? 0);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return Number.POSITIVE_INFINITY;
      return hours * 60 + minutes;
    };

    return meetings
      .filter((meeting) => meeting.meeting_date === dateStr)
      .slice()
      .sort((a, b) => Number(b.booking_mode === 'open_day') - Number(a.booking_mode === 'open_day') || toMinutes(a.meeting_time) - toMinutes(b.meeting_time));
  };

  const toLocalDate = (yyyyMmDd: string) => {
    const raw = (yyyyMmDd || '').trim();
    const parts = raw.split('-');
    if (parts.length !== 3) return new Date(raw);
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) return new Date(raw);
    return new Date(year, month - 1, day);
  };

  const getCustomerName = (meeting: ScheduledMeeting) => {
    const raw = (meeting.customer_name ?? '').toString().trim();
    if (raw) return raw;
    return (meeting.customer_phone ?? '').toString().trim() || 'Cliente';
  };

  const getCustomerFirstName = (meeting: ScheduledMeeting) => {
    const name = getCustomerName(meeting);
    const first = name.split(/\s+/)[0];
    return first || name;
  };

  const getStatusColor = (status: MeetingStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case MeetingStatus.SCHEDULED:
        return 'info';
      case MeetingStatus.CONFIRMED:
        return 'primary';
      case MeetingStatus.COMPLETED:
        return 'success';
      case MeetingStatus.CANCELLED:
        return 'error';
      case MeetingStatus.NO_SHOW:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: MeetingStatus): string => {
    switch (status) {
      case MeetingStatus.SCHEDULED:
        return 'Programada';
      case MeetingStatus.CONFIRMED:
        return 'Confirmada';
      case MeetingStatus.COMPLETED:
        return 'Completada';
      case MeetingStatus.CANCELLED:
        return 'Cancelada';
      case MeetingStatus.NO_SHOW:
        return 'No Asistió';
      default:
        return status;
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const selectedDayMeetings = selectedDate ? getMeetingsForDate(selectedDate) : [];
  const filteredDayMeetings = selectedDayMeetings.filter((meeting) => {
    if (agendaStatus !== 'all' && meeting.status !== agendaStatus) return false;
    const q = agendaQuery.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      meeting.customer_name ?? '',
      meeting.customer_phone ?? '',
      meeting.project_name ?? '',
      meeting.project_code ?? ''
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });

  const meetingCountByDate = meetings.reduce<Record<string, number>>((acc, meeting) => {
    const key = (meeting.meeting_date || '').trim();
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const calendarDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(monthEnd, { weekStartsOn: 1 })
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 0, sm: 1, md: 2 } }}>
      {/* Calendar Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          {format(currentDate, 'MMMM yyyy', { locale: es })}
        </Typography>
        <Box display="flex" alignItems="center" gap={1.5}>
          {refreshing && <CircularProgress size={18} />}
          <Button variant="outlined" size="small" startIcon={<Today />} onClick={handleToday}>
            Hoy
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={7} lg={7}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 }, alignSelf: 'flex-start' }}>
            <Paper
              sx={{
                p: 2,
                width: '100%',
                height: { md: 'calc(100vh - 320px)' },
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '& .MuiCalendarPicker-root': {
                  width: '100% !important',
                  maxWidth: 'none',
                  maxHeight: 'none',
                  margin: '0 !important',
                  flex: 1
                },
                '& .MuiCalendarPicker-viewTransitionContainer': { width: '100%' },
                '& .MuiDayPicker-header': { justifyContent: 'space-between', px: 1 },
                '& .MuiDayPicker-weekContainer': { justifyContent: 'space-between', px: 1 },
                '& .MuiDayPicker-weekDayLabel': {
                  width: 52,
                  height: 40,
                  fontSize: '0.85rem'
                },
                '& .MuiPickersDay-root': {
                  width: 52,
                  height: 52,
                  margin: '0 2px',
                  fontSize: '0.95rem'
                },
                '& .MuiDayPicker-slideTransition': { minHeight: 336 },
                '& .MuiPickersCalendarHeader-label': { fontSize: '1rem' }
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <CalendarPicker
                  date={selectedDate}
                  onChange={(newValue) => {
                    setSelectedDate(newValue);
                    if (newValue) setCurrentDate(newValue);
                  }}
                  openTo="day"
                  views={['year', 'month', 'day']}
                  showDaysOutsideCurrentMonth={false}
                  onMonthChange={(newMonth) => setCurrentDate(newMonth)}
                  renderDay={(day: Date, _value: Array<Date | null>, dayProps: PickersDayProps<Date>) => {
                    const key = format(day, 'yyyy-MM-dd');
                    const count = meetingCountByDate[key] || 0;
                    const badgeContent = count > 9 ? '9+' : count;
                    return (
                      <Badge
                        key={key}
                        overlap="circular"
                        color="primary"
                        badgeContent={badgeContent}
                        invisible={count === 0 || dayProps.outsideCurrentMonth}
                      >
                        <PickersDay {...dayProps} />
                      </Badge>
                    );
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Box>
        </Grid>

        <Grid item xs={12} md={5} lg={5}>
          <Paper
            sx={{
              p: 2,
              height: { md: 'calc(100vh - 320px)' },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {selectedDate ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Citas para {format(selectedDate, 'dd MMMM yyyy', { locale: es })}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar cliente, proyecto o teléfono"
                    value={agendaQuery}
                    onChange={(e) => setAgendaQuery(e.target.value)}
                    fullWidth
                  />
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="agenda-status-filter-label">Estado</InputLabel>
                    <Select
                      labelId="agenda-status-filter-label"
                      value={agendaStatus}
                      label="Estado"
                      onChange={(e) => setAgendaStatus(e.target.value as 'all' | MeetingStatus)}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value={MeetingStatus.SCHEDULED}>{getStatusLabel(MeetingStatus.SCHEDULED)}</MenuItem>
                      <MenuItem value={MeetingStatus.CONFIRMED}>{getStatusLabel(MeetingStatus.CONFIRMED)}</MenuItem>
                      <MenuItem value={MeetingStatus.COMPLETED}>{getStatusLabel(MeetingStatus.COMPLETED)}</MenuItem>
                      <MenuItem value={MeetingStatus.CANCELLED}>{getStatusLabel(MeetingStatus.CANCELLED)}</MenuItem>
                      <MenuItem value={MeetingStatus.NO_SHOW}>{getStatusLabel(MeetingStatus.NO_SHOW)}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {filteredDayMeetings.length === 0 ? (
                  <Typography color="text.secondary">
                    {selectedDayMeetings.length === 0
                      ? 'No hay citas programadas para este día'
                      : 'No hay citas que coincidan con el filtro'}
                  </Typography>
                ) : (
                  <Box sx={{ flex: 1, overflowY: { md: 'auto' }, pr: 0.5 }}>
                    <Grid container spacing={1.5}>
                      {filteredDayMeetings.map((meeting, index) => (
                        <Grid item xs={12} key={meeting.id}>
                          {(index === 0 || filteredDayMeetings[index - 1].booking_mode !== meeting.booking_mode) && (
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {meeting.booking_mode === 'open_day' ? 'Visitas sin hora fija' : 'Citas por hora'}
                            </Typography>
                          )}
                          <Card variant="outlined">
                            <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                                  {formatMeetingTime(meeting)} · {getCustomerName(meeting)}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => handleMeetingClick(meeting)}
                                  sx={{ flexShrink: 0, px: 0.5, minWidth: 'auto' }}
                                >
                                  Ver detalles
                                </Button>
                              </Box>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {meeting.project_name}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </>
            ) : (
              <Typography color="text.secondary">
                Selecciona un día para ver la agenda
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Legacy calendar grid (replaced by StaticDatePicker) */}
      {false && (
      <Grid container spacing={1} columns={7}>
        {/* Weekday Headers */}
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day) => (
          <Grid item xs={1} key={day}>
            <Box
              sx={{
                p: 1,
                textAlign: 'center',
                fontWeight: 'bold',
                color: 'text.secondary',
                borderBottom: 1,
                borderColor: 'divider'
              }}
            >
              {day}
            </Box>
          </Grid>
        ))}

        {/* Calendar Days */}
        {calendarDays.map((day) => {
          const inCurrentMonth = isSameMonth(day, currentDate);
          const dayMeetings = inCurrentMonth ? getMeetingsForDate(day) : [];
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isPast = isBefore(day, startOfDay(new Date()));

          return (
            <Grid item xs={1} key={day.toISOString()}>
              <Paper
                sx={{
                  minHeight: 120,
                  p: 1,
                  cursor: inCurrentMonth ? 'pointer' : 'default',
                  backgroundColor: isSelected ? 'action.selected' : 
                                  isToday(day) ? 'primary.light' : 
                                  isPast ? 'action.hover' : 'background.paper',
                  opacity: inCurrentMonth ? 1 : 0.35,
                  '&:hover': {
                    backgroundColor: inCurrentMonth ? 'action.hover' : 'background.paper'
                  }
                }}
                onClick={() => {
                  if (!inCurrentMonth) return;
                  handleDateClick(day);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isToday(day) ? 'bold' : 'normal',
                    color: isPast ? 'text.secondary' : 'text.primary',
                    mb: 1
                  }}
                >
                  {inCurrentMonth ? format(day, 'd') : ''}
                </Typography>

                {/* Meetings for this day */}
                <Box>
                  {dayMeetings.slice(0, 3).map((meeting) => (
                    <Tooltip
                      key={meeting.id}
                      title={`${getCustomerName(meeting)} - ${formatMeetingTime(meeting)} - ${meeting.project_name}`}
                    >
                      <Chip
                        size="small"
                        label={`${formatMeetingTime(meeting)} ${getCustomerFirstName(meeting)}`}
                        color={getStatusColor(meeting.status as MeetingStatus)}
                        sx={{
                          mb: 0.5,
                          fontSize: '0.7rem',
                          height: 20,
                          cursor: 'pointer',
                          display: 'block',
                          textAlign: 'center'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMeetingClick(meeting);
                        }}
                      />
                    </Tooltip>
                  ))}
                  {dayMeetings.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{dayMeetings.length - 3} más
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      )}

      {/* Meeting Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalles de la Cita
        </DialogTitle>
        <DialogContent>
          {selectedMeeting && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Información del Cliente
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Nombre:</strong> {getCustomerName(selectedMeeting)}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Teléfono:</strong> {selectedMeeting.customer_phone}
                </Typography>
                {selectedMeeting.customer_email && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Email:</strong> {selectedMeeting.customer_email}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Información de la Cita
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Fecha:</strong> {format(toLocalDate(selectedMeeting.meeting_date), 'dd MMMM yyyy', { locale: es })}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Hora:</strong> {formatMeetingTime(selectedMeeting)}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Estado:</strong> 
                  <Chip
                    label={getStatusLabel(selectedMeeting.status as MeetingStatus)}
                    color={getStatusColor(selectedMeeting.status as MeetingStatus)}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Información del Proyecto
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Proyecto:</strong> {selectedMeeting.project_name}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Código:</strong> {selectedMeeting.project_code}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Showroom:</strong> {selectedMeeting.showroom_location}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Contacto de Ventas
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Nombre:</strong> {selectedMeeting.sales_contact_name}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Teléfono:</strong> {selectedMeeting.sales_contact_phone}
                </Typography>
                {selectedMeeting.sales_contact_email && (
                  <Typography variant="body1" gutterBottom>
                    <strong>Email:</strong> {selectedMeeting.sales_contact_email}
                  </Typography>
                )}
              </Grid>

              {selectedMeeting.customer_notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notas del Cliente
                  </Typography>
                  <Typography variant="body1">
                    {selectedMeeting.customer_notes}
                  </Typography>
                </Grid>
              )}

              {selectedMeeting.internal_notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notas Internas
                  </Typography>
                  <Typography variant="body1">
                    {selectedMeeting.internal_notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleStatusUpdate}
          >
            Actualizar Estado
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateDialogOpen} onClose={() => setStatusUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Actualizar Estado de la Cita
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as MeetingStatus)}
                  label="Estado"
                >
                  <MenuItem value={MeetingStatus.SCHEDULED}>Programada</MenuItem>
                  <MenuItem value={MeetingStatus.CONFIRMED}>Confirmada</MenuItem>
                  <MenuItem value={MeetingStatus.COMPLETED}>Completada</MenuItem>
                  <MenuItem value={MeetingStatus.CANCELLED}>Cancelada</MenuItem>
                  <MenuItem value={MeetingStatus.NO_SHOW}>No Asistió</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notas"
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Agregar notas sobre el cambio de estado..."
              />
            </Grid>

            {newStatus === MeetingStatus.CANCELLED && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Razón de Cancelación"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Especificar la razón de la cancelación..."
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveStatusUpdate}
          >
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingCalendar;
